/**
 * Custom react-flow node renderers for the mesh.
 *
 * Three visual node types:
 *   - "mesh"       — agent or synthetic node, shaped per type
 *   - "repoHeader" — swimlane label for tenant scope
 *
 * Risk-overlay colorization is computed per-node from `risk_score` +
 * `findings_severity_max`. Synthetic nodes get a dashed border and
 * a neutral color regardless of risk overlay — they aren't owned
 * agents so risk attribution doesn't apply.
 */
import { Handle, Position, type NodeProps } from '@xyflow/react'
import { Box, Chip, Stack, Tooltip, Typography } from '@mui/material'
import {
  MemoryOutlined, StorageOutlined, BuildOutlined,
  PublicOutlined, AutoAwesomeOutlined,
  LibraryBooksOutlined, PsychologyOutlined,
  HubOutlined, BadgeOutlined, CloudOutlined,
} from '@mui/icons-material'
import type { MeshNode, MeshRepo, OverlayMode, Severity } from './types'

const SEVERITY_COLOR: Record<Severity, string> = {
  critical: '#d32f2f',
  high: '#ed6c02',
  medium: '#f9a825',
  low: '#0288d1',
  info: '#9e9e9e',
}

// Criticality is DECLARED by a person; severity is MEASURED by a scan. They
// share a vocabulary ("critical", "high") and mean different things, and the
// criticality chip used to borrow SEVERITY_COLOR — so a business judgement
// somebody typed rendered in exactly the red a detected critical finding
// does, and the canvas gave no way to tell them apart.
//
// Outlined and desaturated rather than filled: still ranked, visibly stated
// rather than found.
const CRITICALITY_COLOR: Record<string, string> = {
  critical: '#6a1b9a',
  high: '#7b1fa2',
  normal: '#8e24aa',
  low: '#ab47bc',
}

// Stable color palette for framework overlay — derived from MUI palette
// so the chips read on both light + dark theme.
const FRAMEWORK_COLORS: Record<string, string> = {
  langchain: '#1976d2',
  langgraph: '#0288d1',
  crewai: '#7b1fa2',
  autogen: '#388e3c',
  openai: '#00897b',
  anthropic: '#5d4037',
  anthropic_sdk: '#5d4037',
  claude_code: '#3f51b5',
  semantic_kernel: '#c2185b',
  pydantic_ai: '#f57c00',
  mcp: '#455a64',
  a2a: '#37474f',
  // Server-as-agent synthesis (2026-05-31): standalone MCP and A2A server
  // files synthesized as their own agent type. Distinct shades from the
  // synthetic `mcp` / `a2a` palette values so the framework overlay makes
  // the distinction visible.
  mcp_server: '#37474f',
  a2a_server: '#263238',
}

// Provider palette for llm_model nodes — Phase A of §16 V1 enrichment.
// Tuned for clear visual distinction across the 7 major providers a
// typical enterprise mix touches.
/**
 * What each node type looks like on the canvas, as data.
 *
 * Exported because the legend has to say the same thing the canvas draws. It
 * did not: shape and colour were decided here and described nowhere, apart
 * from one sentence of prose on a single lens with no visual key — so a
 * reader looking at a screen of boxes had no way to tell an agent from a
 * store from a tool. Two copies of this mapping would drift the first time a
 * colour changed, which is why the legend imports it rather than repeating it.
 */
/** §25 model-layer colour. Its own hue rather than a shade of the knowledge
 *  slate, because a served model and a vector collection are different things
 *  a reader looks for on different days. */
export const MODEL_LAYER_COLOR = '#00695c'

/** The component kinds that are the model layer rather than the knowledge
 *  layer. Both arrive as `knowledge_component` nodes — they are components —
 *  so the distinction is the kind, not the node type. */
export const MODEL_LAYER_KINDS = ['inference_endpoint', 'model_artifact']

export const isModelLayer = (node: MeshNode): boolean =>
  node.type === 'knowledge_component'
  && !!node.component_kind
  && MODEL_LAYER_KINDS.includes(node.component_kind)

/**
 * The key to what a node IS. `componentKind` narrows an entry to one kind of
 * component where the node type alone is too coarse.
 *
 * The model layer needed that. An inference endpoint and a model artifact are
 * `knowledge_component` nodes, so they drew as the same dark slate drum as a
 * Qdrant collection and the legend called them "knowledge component" — while
 * the word "model" was already spent on `llm_model`, which is the model an
 * agent's code DECLARES. Looking for a served model meant hunting for
 * something both mislabelled and namesake-collided, which is exactly the
 * report that prompted this.
 */
export const NODE_TYPE_KEY: Array<{
  type: string
  componentKind?: string
  label: string
  color: string
  shape: 'agent' | 'store' | 'pill'
}> = [
  { type: 'agent', label: 'agent', color: '#546e7a', shape: 'agent' },
  // Before the generic knowledge entry, so the more specific match wins when
  // a legend is built by walking this list.
  {
    type: 'knowledge_component', componentKind: 'inference_endpoint',
    label: 'served model', color: MODEL_LAYER_COLOR, shape: 'store',
  },
  {
    type: 'knowledge_component', componentKind: 'model_artifact',
    label: 'model weights', color: MODEL_LAYER_COLOR, shape: 'store',
  },
  { type: 'knowledge_component', label: 'knowledge component', color: '#455a64', shape: 'store' },
  { type: 'tool', label: 'tool', color: '#00838f', shape: 'pill' },
  // "declared" because it is what the agent's code names, as distinct from a
  // model this estate serves or holds. The two were both "model".
  { type: 'llm_model', label: 'declared model', color: '#1976d2', shape: 'pill' },
  { type: 'memory_store', label: 'memory', color: '#8e24aa', shape: 'pill' },
  { type: 'external_api', label: 'external API', color: '#ef6c00', shape: 'pill' },
  { type: 'platform_gateway', label: 'platform', color: '#546e7a', shape: 'pill' },
]

/** Which legend entry a node belongs to — most specific first. */
export const legendEntryFor = (node: MeshNode) =>
  NODE_TYPE_KEY.find(
    (e) => e.type === node.type && e.componentKind === node.component_kind,
  )
  || NODE_TYPE_KEY.find((e) => e.type === node.type && !e.componentKind)
  || (node.type.startsWith('platform_')
    ? NODE_TYPE_KEY.find((e) => e.type === 'platform_gateway')
    : undefined)

const PROVIDER_COLORS: Record<string, string> = {
  openai: '#00897b',
  anthropic: '#ed6c02',
  google: '#1976d2',
  azure: '#0288d1',
  bedrock: '#fbc02d',
  cohere: '#7b1fa2',
  mistral: '#5e35b1',
  meta: '#1565c0',
  local: '#607d8b',
  unknown: '#90a4ae',
}

// Dependency-node icon mapping — small, monochrome glyph in the upper-left
// of the chip so the type reads at glance even when the canvas is zoomed out.
function NodeTypeIcon({ type, color }: { type: string; color: string }) {
  const sx = { fontSize: 14, color }
  switch (type) {
    case 'llm_model': return <AutoAwesomeOutlined sx={sx} />
    case 'tool': return <BuildOutlined sx={sx} />
    case 'memory_store': return <MemoryOutlined sx={sx} />
    case 'external_api': return <PublicOutlined sx={sx} />
    case 'mcp_server': return <StorageOutlined sx={sx} />
    case 'inference_endpoint': return <PsychologyOutlined sx={sx} />
    case 'model_artifact': return <PsychologyOutlined sx={sx} />
    case 'knowledge_component': return <LibraryBooksOutlined sx={sx} />
    case 'platform_gateway': return <HubOutlined sx={sx} />
    case 'platform_memory': return <MemoryOutlined sx={sx} />
    case 'platform_identity': return <BadgeOutlined sx={sx} />
    case 'platform_service': return <CloudOutlined sx={sx} />
    default: return null
  }
}

// One colour per hosting layer, used by the platform-service pills. There was
// briefly a Platform lens colouring agents by these; it was removed because
// hosting is already on the agent card and the Deployment card, and on a
// self-hosted estate every node came out the same grey — which reads as no
// data rather than as an answer.
const PLATFORM_COLORS: Record<string, string> = {
  bedrock_agentcore: '#ff9900',   // AWS
  vertex_agent_engine: '#4285f4', // Google
  azure_ai_foundry: '#0078d4',    // Microsoft
  cloudflare_workers: '#f6821f',
}

function frameworkColor(fw: string | null | undefined): string {
  if (!fw) return '#90a4ae'
  return FRAMEWORK_COLORS[fw] || '#90a4ae'
}

function riskColor(node: MeshNode): string {
  if (node._source === 'synthetic') return '#cfd8dc'
  if (node.findings_severity_max) return SEVERITY_COLOR[node.findings_severity_max]
  if (node.risk_score > 0) return '#fdd835'
  return '#cfd8dc'
}

// UX1 (2026-06-01): Blast Radius gets its OWN cool-to-hot color gradient
// distinct from Risk. Without this, width-scaling alone gets lost in
// the visual noise when every agent inherits the same workspace MCP
// config and ends up with similar downstream_count.
function blastRadiusColor(node: MeshNode): string {
  if (node._source === 'synthetic') return '#cfd8dc'
  const n = node.blast_radius ?? node.downstream_count ?? 0
  if (n === 0) return '#cfd8dc'
  if (n === 1) return '#90a4ae'
  if (n <= 3) return '#0288d1'
  if (n <= 7) return '#7b1fa2'
  return '#d32f2f'
}

// UX1 — Drift overlay needs to make non-changed agents visually RECEDE
// so the glowing changed ones stand out.
//
// P1a (2026-06-01) — when the authoritative drift_verdict from the
// analytics service is available, render verdict-specific colors:
//   CRITICAL_DRIFT  → red       (model swap, new code_exec tool, etc.)
//   MATERIAL_DRIFT  → amber     (tool count change, same-family model bump)
//   COSMETIC_DRIFT  → pale amber (goal text reworded, no behavior impact)
//   POSITIVE_DRIFT  → green     (error posture improved, no new risk)
//   REBASELINE      → blue      (ABF schema upgraded, no real drift)
//   STABLE          → light gray
// Fall back to the boolean drift_recent for tenants whose analytics
// service didn't return a verdict (network failure or pre-v2 baseline).
function driftColor(node: MeshNode): string {
  if (node._source === 'synthetic') return '#cfd8dc'
  if (node.drift_improvement === 'POSITIVE_DRIFT') return '#388e3c'
  switch (node.drift_verdict) {
    case 'CRITICAL_DRIFT': return '#d32f2f'
    case 'MATERIAL_DRIFT': return '#fb8c00'
    case 'COSMETIC_DRIFT': return '#ffc107'
    case 'REBASELINE': return '#1976d2'
    case 'STABLE': return '#e0e0e0'
    default: return node.drift_recent ? '#fb8c00' : '#e0e0e0'
  }
}

// M5b — Compliance Lens: 4-color border keyed off compliance_status.
// Distinct from the severity-based risk color so the visual idiom
// (color meaning) reads as "compliance buckets" not "severity gradient."
const COMPLIANCE_COLOR: Record<string, string> = {
  aligned: '#388e3c',    // green
  partial: '#fb8c00',    // amber
  gap: '#d32f2f',        // red
  not_checked: '#bdbdbd', // gray
}

function complianceColor(node: MeshNode): string {
  if (node._source === 'synthetic') return '#cfd8dc'
  const status = (node.compliance_status as string | null) || 'not_checked'
  return COMPLIANCE_COLOR[status] || COMPLIANCE_COLOR.not_checked
}

interface MeshNodeData extends Record<string, unknown> {
  /** Set when the legend is emphasising another node type. */
  dimmed?: boolean
  node: MeshNode
  overlay?: OverlayMode
  selected?: boolean
}

export function MeshNodeView(props: NodeProps) {
  const { node, dimmed } = props.data as MeshNodeData
  // Dimmed rather than hidden when the legend picks out a type. Hiding
  // collapses the layout and re-runs it, which loses the spatial memory a
  // reader has just built — and the question "where is the model" is usually
  // followed by "and what connects to it", which needs the neighbours still
  // on screen. Existing zoom-to-neighbourhood already covers the hide case.
  const view = <MeshNodeInner {...props} />
  return dimmed
    ? <Box sx={{ opacity: 0.18, filter: 'grayscale(0.6)', transition: 'opacity .15s' }}>{view}</Box>
    : view
}

function MeshNodeInner(props: NodeProps) {
  const { node } = props.data as MeshNodeData
  // Phase A-D dependency nodes render as compact pills instead of the
  // full agent card — keeps the canvas readable when a single agent
  // points at 12 tools and 3 model nodes.
  if (
    node.type === 'llm_model' ||
    node.type === 'tool' ||
    node.type === 'memory_store' ||
    node.type === 'external_api' ||
    node.type === 'knowledge_component' ||
    node.type === 'platform_gateway' ||
    node.type === 'platform_memory' ||
    node.type === 'platform_identity' ||
    node.type === 'platform_service'
  ) {
    return <DependencyPillView {...props} />
  }
  return <AgentNodeView {...props} />
}


function AgentNodeView({ data, selected }: NodeProps) {
  const { node, overlay = 'risk' } = data as MeshNodeData
  const isSynthetic = node._source === 'synthetic'
  const isAgent = node.type === 'agent'

  // Per-overlay color resolution. Risk + Framework use color on border;
  // Blast Radius uses node SIZE (distinct visual axis from color);
  // Drift adds a pulsing glow on top of whatever the base overlay is.
  const color =
    overlay === 'framework' ? frameworkColor(node.framework) :
    overlay === 'compliance' ? complianceColor(node) :
    overlay === 'blast_radius' ? blastRadiusColor(node) :
    overlay === 'drift' ? driftColor(node) :
    riskColor(node)

  // UX1 — when in Drift mode and this node hasn't drifted, dim the whole
  // card so the glowing changed nodes really pop. Pure visual focus.
  // P1a — dim nodes that didn't drift OR are explicitly STABLE so the
  // attention-worthy verdicts (critical/material/cosmetic/positive) pop.
  const driftDimmed =
    overlay === 'drift'
    && node.drift_improvement !== 'POSITIVE_DRIFT'
    && (node.drift_verdict === 'STABLE'
        || node.drift_verdict === null
        || node.drift_verdict === undefined
          ? !node.drift_recent
          : false)

  const totalFindings = node.findings_count + node.affected_findings_count

  // M5a — Blast Radius: scale width with downstream_count. Stepped buckets
  // keep the layout from oscillating between scans (200..280px).
  const downstream = node.blast_radius ?? node.downstream_count ?? 0
  const blastRadiusWidth = overlay === 'blast_radius'
    ? downstream >= 8 ? 280 : downstream >= 4 ? 250 : downstream >= 2 ? 230 : 210
    : 220
  const blastRadiusMinHeight = overlay === 'blast_radius'
    ? downstream >= 8 ? 110 : downstream >= 4 ? 100 : downstream >= 2 ? 95 : 88
    : 88

  // M5a — Drift: pulsing amber glow on drift_recent nodes. Implemented as
  // a CSS box-shadow keyframe so it works without canvas-wide JS animation.
  const isDrifted = overlay === 'drift' && node.drift_recent === true

  return (
    <Box
      sx={{
        width: blastRadiusWidth,
        minHeight: blastRadiusMinHeight,
        bgcolor: 'background.paper',
        border: '2px solid',
        borderColor: selected ? 'primary.main' : color,
        borderStyle: isSynthetic ? 'dashed' : 'solid',
        borderRadius: 1.5,
        boxShadow: selected ? 4 : isDrifted ? '0 0 0 0 rgba(255, 152, 0, 0.6)' : 1,
        animation: isDrifted ? 'meshDriftPulse 1.8s ease-in-out infinite' : 'none',
        '@keyframes meshDriftPulse': {
          '0%, 100%': { boxShadow: '0 0 0 0 rgba(255, 152, 0, 0.6)' },
          '50%':      { boxShadow: '0 0 0 6px rgba(255, 152, 0, 0)' },
        },
        p: 1,
        cursor: 'pointer',
        transition: 'all 0.12s',
        opacity: driftDimmed ? 0.45 : 1,
        '&:hover': { boxShadow: 3, opacity: 1 },
      }}
    >
      <Handle type="target" position={Position.Left} style={{ background: '#90a4ae' }} />
      <Handle type="source" position={Position.Right} style={{ background: '#90a4ae' }} />

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
          {isAgent ? node.framework || 'agent' : node.type.replace('_', ' ')}
        </Typography>
        {node.autonomy_level && (
          <Chip
            size="small"
            label={node.autonomy_level}
            sx={{ height: 18, fontSize: 10, bgcolor: 'grey.100' }}
          />
        )}
      </Stack>

      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 600,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          lineHeight: 1.25,
        }}
      >
        {node.name || '(unnamed)'}
      </Typography>

      {node.annotations?.owner && (
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
          @{node.annotations.owner}
          {node.annotations.team ? ` · ${node.annotations.team}` : ''}
        </Typography>
      )}

      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
        {totalFindings > 0 && (
          <Tooltip
            title={`${node.findings_count} direct · ${node.affected_findings_count} multi-agent`}
            placement="top"
          >
            <Chip
              size="small"
              label={`${totalFindings} findings`}
              sx={{
                height: 18,
                fontSize: 10,
                bgcolor: color,
                color: '#fff',
              }}
            />
          </Tooltip>
        )}
        {node.tools.length > 0 && (
          <Chip
            size="small"
            label={`${node.tools.length} tools`}
            sx={{ height: 18, fontSize: 10, bgcolor: 'grey.100' }}
          />
        )}
        {/* Direction matters and the arrow carries it. An agent reaches
            outward; a store is reached. Showing both as "→ 11" would make a
            store eleven agents depend on look identical to an agent that
            depends on eleven things — opposite situations with opposite
            remediations. */}
        {overlay === 'blast_radius' && downstream > 0 && (
          <Tooltip
            title={(node.dependent_count ?? 0) > (node.downstream_count ?? 0)
              ? `${node.dependent_count} agents depend on this. If it is stale, wrong or compromised, that is who is affected.`
              : `Touches ${downstream} downstream nodes if it misbehaves`}
            placement="top"
          >
            <Chip
              size="small"
              label={(node.dependent_count ?? 0) > (node.downstream_count ?? 0)
                ? `← ${node.dependent_count}`
                : `→ ${downstream}`}
              sx={{
                height: 18,
                fontSize: 10,
                fontWeight: 700,
                bgcolor: downstream >= 8 ? '#7b1fa2' : downstream >= 4 ? '#1976d2' : downstream >= 2 ? '#0288d1' : '#90a4ae',
                color: '#fff',
              }}
            />
          </Tooltip>
        )}
        {node.annotations?.criticality && (
          <Tooltip title={
            `Criticality stated by ${node.annotations.updated_by || 'a team member'}`
            + ' — declared, not detected'
          }>
            <Chip
              size="small"
              variant="outlined"
              label={node.annotations.criticality}
              sx={{
                height: 18,
                fontSize: 10,
                borderColor: CRITICALITY_COLOR[node.annotations.criticality] || '#bdbdbd',
                color: CRITICALITY_COLOR[node.annotations.criticality] || '#bdbdbd',
                bgcolor: 'transparent',
              }}
            />
          </Tooltip>
        )}
      </Stack>
    </Box>
  )
}

/**
 * Compact pill renderer for §16 V1 enrichment dependency nodes
 * (llm_model, tool, memory_store, external_api). Smaller footprint
 * than agent nodes so a single agent calling 12 tools doesn't blow
 * out the canvas. Color + icon read the node's type at a glance.
 */
function DependencyPillView({ data, selected }: NodeProps) {
  const { node, overlay = 'risk' } = data as MeshNodeData
  let color = '#90a4ae'
  let subtitle: string | null = null
  if (node.type === 'llm_model') {
    color = PROVIDER_COLORS[node.provider || 'unknown'] || PROVIDER_COLORS.unknown
    subtitle = node.provider && node.provider !== 'unknown' ? node.provider : null
  } else if (node.type === 'tool') {
    color = '#00838f' // muted teal
  } else if (node.type === 'memory_store') {
    color = '#8e24aa' // lavender
    subtitle = node.memory_category || null
  } else if (node.type === 'external_api') {
    // Domain markers — external (third-party) is amber; configurable
    // (can be self-hosted or SaaS) is blue-gray; internal is muted.
    color = node.api_domain === 'external'
      ? '#ef6c00'
      : node.api_domain === 'configurable'
      ? '#546e7a'
      : '#90a4ae'
    subtitle = node.api_kind ? node.api_kind.replace('_', ' ') : null
  } else if (isModelLayer(node)) {
    // §25. Its own hue, so a served model does not read as a vector store —
    // the report that prompted this was simply "it is hard to spot".
    color = MODEL_LAYER_COLOR
    subtitle = node.component_kind === 'inference_endpoint'
      ? 'served model'
      : 'model weights'
  } else if (node.type === 'knowledge_component') {
    // One colour for every knowledge component, classified or not. Red here
    // meant "holds sensitive data", while red in the Risk lens means "has a
    // critical finding" — two meanings on one canvas, and a reader cannot
    // hold both. A classified store is a fact; the finding is an ungated
    // agent reaching it, and that already colours the *agent* through the
    // risk overlay, which is where a reader can act on it.
    color = '#455a64'
    subtitle = node.classification
      || (node.component_kind ? node.component_kind.replace(/_/g, ' ') : null)
  } else if (node.type.startsWith('platform_')) {
    // Coloured by the platform that provides it, so a Gateway reads as part
    // of the same hosting layer as the agents pointing at it.
    color = PLATFORM_COLORS[node.platform || ''] || '#546e7a'
    subtitle = node.platform ? node.platform.replace(/_/g, ' ') : null
  }

  // Shape carries what a node *is*; colour carries whatever lens is active.
  // Keeping them on separate channels is the only way both can be read at
  // once — colour already means severity under Risk, framework under
  // Framework and a compliance verdict under Compliance, so a node type
  // encoded in colour would change meaning every time the lens does.
  //
  // A knowledge component gets the drum outline every diagramming convention
  // has used for a datastore since the 1970s. Until now every dependency
  // rendered as the same rounded rectangle, distinguished by a 14px icon that
  // is unreadable at the zoom people actually use.
  const isStore = node.type === 'knowledge_component'

  // Blast Radius sizes by dependents as well as by reach, and these nodes are
  // where that matters most: a store eleven agents read is the reason the
  // lens went bidirectional. It was sizing agents only, so the store — the
  // subject of the question — stayed the same width as everything else.
  const blast = node.blast_radius ?? node.downstream_count ?? 0
  const pillWidth = overlay === 'blast_radius'
    ? Math.min(300, 150 + blast * 12)
    : 180

  return (
    <Box
      sx={{
        width: pillWidth,
        minHeight: 44,
        bgcolor: 'background.paper',
        border: '2px solid',
        borderColor: selected ? 'primary.main' : color,
        borderStyle: 'dashed',
        borderRadius: isStore ? '16px / 50%' : 8,
        boxShadow: selected ? 3 : 0,
        px: 1.5,
        py: 0.75,
        cursor: 'pointer',
        transition: 'all 0.12s',
        '&:hover': { boxShadow: 2 },
      }}
    >
      <Handle type="target" position={Position.Left} style={{ background: color }} />
      <Stack direction="row" spacing={0.75} alignItems="center">
        {/* The component KIND where there is one, so a served model gets a
            different glyph from a vector collection — both are
            `knowledge_component` nodes and the type alone cannot tell them
            apart. */}
        <NodeTypeIcon
          type={isModelLayer(node) ? node.component_kind! : node.type}
          color={color}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="caption"
            sx={{ display: 'block', color, fontWeight: 700, fontSize: 10, lineHeight: 1.1 }}
          >
            {node.type.replace('_', ' ')}
          </Typography>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              fontSize: 12,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              lineHeight: 1.2,
            }}
          >
            {node.name || '(unnamed)'}
          </Typography>
          {subtitle && (
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', fontSize: 10, display: 'block' }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
      </Stack>
    </Box>
  )
}

interface RepoHeaderData extends Record<string, unknown> {
  repo: MeshRepo
  width: number
}

export function RepoHeaderView({ data }: NodeProps) {
  const { repo, width } = data as RepoHeaderData
  return (
    <Box
      sx={{
        width: width || 200,
        px: 1.5,
        py: 0.5,
        bgcolor: 'primary.main',
        color: 'primary.contrastText',
        borderRadius: '4px 4px 0 0',
        fontSize: 12,
        fontWeight: 600,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {repo.name} · {repo.agent_count} agents
      {repo.scan_count !== undefined && repo.scan_count > 1 && ` · ${repo.scan_count} scans`}
    </Box>
  )
}
